import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Check, ExternalLink, X } from 'lucide-react'
import {
  MODERATION_REASON_MAX,
  salonModerationRequestSchema,
  type SalonModerationResult,
} from '@manaro/contracts'
import { ApiError } from '@/shared/api/client.ts'
import { cn } from '@/shared/lib/cn.ts'
import { formatDate, formatRelative } from '@/shared/lib/format.ts'
import { Avatar } from '@/shared/ui/avatar.tsx'
import { Badge } from '@/shared/ui/badge.tsx'
import { Button } from '@/shared/ui/button.tsx'
import { Alert } from '@/shared/ui/states.tsx'
import { bookingPageUrl } from '../labels.ts'
import {
  MODERATION_ACTION_LABELS,
  MODERATION_LABELS,
  MODERATION_TONES,
  QUICK_REASONS,
  appendReason,
  languageName,
  type ModeratableRow,
} from '../moderation.ts'
import { ownerContactLinks, salonContactLinks } from '../contacts.ts'
import { useModerateSalon } from '../use-salons.ts'
import { ContactButtons } from './contact-links.tsx'

type Moderation = ModeratableRow['salon']['moderation']
type Mode = 'review' | 'reject' | 'unpublish'

interface ModerationDialogProps {
  row: ModeratableRow
  onClose: () => void
  onDone: (result: SalonModerationResult) => void
}

function ExternalLinkTo({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex min-h-8 items-center gap-1 text-[13px] text-brand-ink hover:text-brand hover:underline"
    >
      {children}
      <ExternalLink className="size-3.5" aria-hidden />
    </a>
  )
}

/** What a moderator looks at before publishing: present or missing, with the count when there is one. */
function CheckItem({ ok, children }: { ok: boolean; children: ReactNode }) {
  return (
    <li className="flex items-center gap-2">
      {ok ? (
        <Check role="img" className="size-4 shrink-0 text-brand" aria-label="есть" />
      ) : (
        <X role="img" className="size-4 shrink-0 text-warn-ink" aria-label="нет" />
      )}
      <span className={cn(!ok && 'text-warn-ink')}>{children}</span>
    </li>
  )
}

/** The salon photo, or its initials when there is none or it does not load. */
function SalonPicture({ title, image }: { title: string; image: string | null }) {
  const [failed, setFailed] = useState(false)

  if (!image || failed) return <Avatar name={title} className="size-14 rounded-xl text-[17px]" />
  return (
    <img
      src={image}
      alt=""
      onError={() => setFailed(true)}
      className="size-14 shrink-0 rounded-xl border border-line-soft bg-surface-soft object-cover"
    />
  )
}

function LastEvent({ moderation }: { moderation: Moderation }) {
  const event = moderation.lastEvent
  if (!event) return null

  const who = event.action === 'resubmitted' ? '' : ` · ${event.actor}`
  return (
    <p className="text-[12.5px] text-ink-muted">
      {MODERATION_ACTION_LABELS[event.action]}
      {who}
      {event.at && ` · ${formatRelative(event.at)}`}
    </p>
  )
}

export function ModerationDialog({ row, onClose, onDone }: ModerationDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const titleRef = useRef<HTMLHeadingElement>(null)
  // A click that started inside (e.g. selecting text) and ended on the backdrop must not close the dialog.
  const pressedOnBackdrop = useRef(false)
  const [mode, setMode] = useState<Mode>('review')
  const [reason, setReason] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const moderate = useModerateSalon()
  const { salon, user } = row
  const { moderation } = salon
  const language = languageName(salon.language)

  // Start on the title, not on the first link: Enter must not take the moderator away from the dialog.
  useEffect(() => {
    dialogRef.current?.showModal()
    titleRef.current?.focus()
  }, [])

  // Someone else decided meanwhile and the list was reloaded: back to the buttons that fit the new state.
  useEffect(() => {
    setMode('review')
  }, [moderation.state])

  function decide(request: unknown) {
    if (moderate.isPending) return

    const parsed = salonModerationRequestSchema.safeParse(request)
    if (!parsed.success) {
      setValidationError(parsed.error.issues[0]?.message ?? 'Напишите владельцу, что исправить.')
      return
    }
    setValidationError(null)
    moderate.mutate(
      { id: salon.id, request: parsed.data },
      {
        onSuccess: (result) => {
          onDone(result)
          dialogRef.current?.close()
        },
      },
    )
  }

  function submitReason(event: FormEvent) {
    event.preventDefault()
    decide({ decision: mode === 'unpublish' ? 'unpublish' : 'reject', reason })
  }

  function openReason(next: Mode) {
    moderate.reset()
    setValidationError(null)
    setMode(next)
  }

  const error =
    validationError ??
    (moderate.error
      ? moderate.error instanceof ApiError
        ? moderate.error.message
        : 'Не получилось — попробуйте ещё раз.'
      : null)

  const canPublish = moderation.state === 'pending' || moderation.state === 'rejected'
  const place = [salon.city, salon.address].filter(Boolean).join(', ')

  const close = () => {
    if (!moderate.isPending) dialogRef.current?.close()
  }

  // The backdrop dismisses the dialog, but never throws away a reason the moderator has started typing.
  const closeFromBackdrop = () => {
    if (mode === 'review' || !reason.trim()) close()
  }

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      onCancel={(event) => moderate.isPending && event.preventDefault()}
      onMouseDown={(event) => {
        pressedOnBackdrop.current = event.target === event.currentTarget
      }}
      onClick={(event) => {
        if (pressedOnBackdrop.current && event.target === event.currentTarget) closeFromBackdrop()
      }}
      aria-labelledby="moderation-dialog-title"
      className="m-auto max-h-[calc(100dvh-24px)] w-[min(560px,calc(100vw-24px))] overflow-y-auto overscroll-contain rounded-panel border border-line bg-surface p-0 text-ink shadow-panel backdrop:bg-[rgb(27_27_24/0.35)]"
    >
      <div className="flex flex-col gap-4 px-5 py-5 md:px-6">
        <header className="flex items-start gap-3.5">
          <SalonPicture title={salon.title} image={salon.image} />
          <div className="min-w-0 flex-1">
            <h2
              id="moderation-dialog-title"
              ref={titleRef}
              tabIndex={-1}
              className="outline-none font-display text-[19px] leading-tight font-semibold tracking-[-0.01em] [overflow-wrap:anywhere]"
            >
              {salon.title}
            </h2>
            {place && <p className="mt-0.5 text-[13px] text-ink-muted [overflow-wrap:anywhere]">{place}</p>}
            <div className="mt-1 flex flex-wrap gap-x-4">
              {salon.publicUrl && <ExternalLinkTo href={salon.publicUrl}>Страница в каталоге</ExternalLinkTo>}
              <ExternalLinkTo href={bookingPageUrl(salon)}>Онлайн-запись</ExternalLinkTo>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            disabled={moderate.isPending}
            aria-label="Закрыть"
            className="-mt-1.5 -mr-2 flex size-11 shrink-0 items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink disabled:opacity-50 md:size-9"
          >
            <X className="size-5" aria-hidden />
          </button>
        </header>

        <section className="flex flex-col gap-1.5">
          <div>
            <Badge tone={MODERATION_TONES[moderation.state]}>{MODERATION_LABELS[moderation.state]}</Badge>
          </div>
          <LastEvent moderation={moderation} />
          {moderation.comment && (
            <p className="rounded-control bg-surface-soft px-3.5 py-2.5 text-[13.5px] whitespace-pre-line [overflow-wrap:anywhere]">
              <span className="text-ink-muted">Замечание владельцу: </span>
              {moderation.comment}
            </p>
          )}
        </section>

        <section className="grid gap-x-6 gap-y-3 text-[13.5px] sm:grid-cols-2">
          <ul className="flex flex-col gap-1.5" aria-label="Что проверить">
            <CheckItem ok={Boolean(salon.image)}>Фото</CheckItem>
            <CheckItem ok={salon.services > 0}>Услуг: {salon.services}</CheckItem>
            <CheckItem ok={salon.masters > 0}>Мастеров: {salon.masters}</CheckItem>
            <CheckItem ok={Boolean(salon.address)}>Адрес</CheckItem>
          </ul>
          <dl className="flex flex-col gap-1.5 [&_dt]:text-ink-muted">
            <div className="flex gap-1.5">
              <dt>Владелец</dt>
              <dd className="min-w-0 truncate" title={user.email}>
                {user.email}
              </dd>
            </div>
            {salon.createdAt && (
              <div className="flex gap-1.5">
                <dt>Создан</dt>
                <dd>{formatDate(salon.createdAt)}</dd>
              </div>
            )}
          </dl>
        </section>

        <section className="flex flex-col gap-1.5">
          <h3 className="text-[13px] text-ink-muted">Связаться с салоном</h3>
          <ContactButtons links={salonContactLinks(salon)} />
        </section>

        <section className="flex flex-col gap-1.5">
          <h3 className="text-[13px] text-ink-muted">Написать владельцу</h3>
          <ContactButtons links={ownerContactLinks(user, salon)} />
        </section>

        {mode === 'review' ? (
          <>
            {error && <Alert>{error}</Alert>}
            <div className="flex flex-col gap-2 border-t border-line-soft pt-4">
              <div className="flex flex-wrap justify-end gap-2 max-sm:[&>button]:min-h-11 max-sm:[&>button]:flex-1">
                {canPublish && (
                  <>
                    <Button variant="secondary" size="sm" disabled={moderate.isPending} onClick={() => openReason('reject')}>
                      Отклонить…
                    </Button>
                    <Button size="sm" loading={moderate.isPending} onClick={() => decide({ decision: 'approve' })}>
                      Опубликовать
                    </Button>
                  </>
                )}
                {moderation.state === 'published' && (
                  <Button variant="secondary" size="sm" onClick={() => openReason('unpublish')}>
                    Снять с публикации…
                  </Button>
                )}
                {!canPublish && (
                  <Button variant="ghost" size="sm" onClick={close}>
                    Закрыть
                  </Button>
                )}
              </div>
              {canPublish && (
                <p className="text-right text-[12px] text-ink-muted">
                  Салон появится в каталоге Manaro, владельцу уйдёт письмо.
                </p>
              )}
            </div>
          </>
        ) : (
          <form onSubmit={submitReason} className="flex flex-col gap-3 border-t border-line-soft pt-4">
            <label className="flex flex-col gap-1.5">
              <span className="flex items-baseline justify-between gap-2.5">
                <span className="text-[13px] font-medium text-ink-soft">Что исправить</span>
                <span className="tabular text-[12px] text-ink-faint">
                  {reason.length}/{MODERATION_REASON_MAX}
                </span>
              </span>
              <span id="moderation-reason-hint" className="text-[12.5px] text-ink-muted text-balance-pretty">
                Владелец увидит этот текст в письме и в CRM{language ? ` — пишите на ${language}` : ''}.
              </span>
              <textarea
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value)
                  setValidationError(null)
                }}
                rows={4}
                aria-describedby="moderation-reason-hint"
                maxLength={MODERATION_REASON_MAX}
                autoFocus
                placeholder="Например: додайте фото салону та послуги з цінами."
                className="resize-y rounded-control border border-line-strong bg-surface-soft px-3.5 py-2.5 text-[16px] text-ink outline-none placeholder:text-ink-muted focus:border-brand focus:shadow-[0_0_0_3px_var(--color-brand-ring)] md:text-[14.5px]"
              />
            </label>

            <div role="group" className="flex flex-wrap gap-1.5" aria-label="Готовые замечания">
              {QUICK_REASONS.map((quick) => (
                <button
                  key={quick}
                  type="button"
                  onClick={() => {
                    setReason((current) => appendReason(current, quick).slice(0, MODERATION_REASON_MAX))
                    setValidationError(null)
                  }}
                  className="min-h-11 rounded-full border border-line bg-surface px-3 py-1 text-left text-[12.5px] text-neutral-ink hover:border-[#d8d2c6] md:min-h-8"
                >
                  {quick}
                </button>
              ))}
            </div>

            {error && <Alert>{error}</Alert>}

            <div className="flex flex-wrap justify-end gap-2 max-sm:[&>button]:min-h-11 max-sm:[&>button]:flex-1">
              <Button variant="secondary" size="sm" disabled={moderate.isPending} onClick={() => openReason('review')}>
                Назад
              </Button>
              <Button type="submit" size="sm" loading={moderate.isPending}>
                {mode === 'unpublish' ? 'Снять и написать владельцу' : 'Отклонить и написать владельцу'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </dialog>
  )
}
